"use client"

import { useRef } from "react"
import type { ThatchDepthBand } from "@/lib/dethatching-calculator"

/**
 * Tappable turf cross-section selector (Claude Design DethatchingPage, section 2).
 * Each option is a to-scale side view of turf: green blades over a brown thatch
 * band over dark soil. Recognition over recall - the user matches a picture,
 * not a measurement.
 */

interface BandDef {
  key: ThatchDepthBand
  label: string
  sub: string
  /** thatch band height in the 96x52 mini SVG */
  h: number
}

const BANDS: BandDef[] = [
  { key: "under-quarter", label: "Thin and springy", sub: "Less than 1/4 inch", h: 3 },
  { key: "quarter-to-half", label: "A light cushion", sub: "About 1/4 up to 1/2 inch", h: 6 },
  { key: "half-to-one", label: "Spongy underfoot", sub: "Over 1/2 and up to 1 inch", h: 12 },
  { key: "over-one", label: "Thick, like carpet", sub: "More than 1 inch", h: 22 },
]

function BandThumb({ band, selected }: { band: BandDef; selected: boolean }) {
  const turfY = 18
  const soilY = turfY + band.h
  const soilH = 48 - soilY
  const dotY = soilY + soilH * 0.45
  const squigY = turfY + band.h * 0.5
  const squigOpacity = band.h > 8 ? 0.8 : 0
  // Heavy thatch starves the blades: gray them out on the worst band.
  const bladeColor = band.key === "over-one" ? "#a3b8a8" : "#16a34a"

  return (
    <svg
      width="96"
      height="52"
      viewBox="0 0 96 52"
      aria-hidden="true"
      className="shrink-0 rounded-lg border border-gray-100 bg-white"
    >
      <g stroke={bladeColor} strokeWidth="1.5" strokeLinecap="round" fill="none">
        <path
          d={`M8 ${turfY}c0-4 1-7 2-9M16 ${turfY}c0-5 1-8 2-10M24 ${turfY}c0-4 1-6 2-8M33 ${turfY}c0-5 1-8 2-10M41 ${turfY}c0-4 1-7 2-9M50 ${turfY}c0-5 1-7 2-9M58 ${turfY}c0-4 1-6 2-8M67 ${turfY}c0-5 1-8 2-10M76 ${turfY}c0-4 1-7 2-9M85 ${turfY}c0-4 1-6 2-8`}
        />
      </g>
      <line x1="4" y1={turfY} x2="92" y2={turfY} stroke="#15803d" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="4" y={turfY} width="88" height={band.h} rx="1.5" fill="#b45309" opacity="0.38" />
      <path
        d={`M10 ${squigY}c3-2 6-2 9-1M26 ${squigY}c3-2 6-2 9-1M46 ${squigY}c3-2 6-2 9-1M66 ${squigY}c3-2 6-2 9-1`}
        stroke="#92400e"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
        opacity={squigOpacity}
      />
      <rect x="4" y={soilY} width="88" height={soilH} fill="#451a03" opacity="0.75" rx="1.5" />
      <circle cx="20" cy={dotY} r="1.2" fill="#92400e" />
      <circle cx="52" cy={dotY} r="1.2" fill="#92400e" />
      <circle cx="78" cy={dotY} r="1.2" fill="#92400e" />
      {selected && (
        <g>
          <circle cx="86" cy="10" r="7" fill="#16a34a" />
          <path d="M83 10.3l2 2 4-4.4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>
      )}
    </svg>
  )
}

export function ThatchBandSelector({
  value,
  onChange,
}: {
  value: ThatchDepthBand | ""
  onChange: (band: ThatchDepthBand) => void
}) {
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([])

  // Roving-tabindex radio behavior (WAI radio pattern): the group is one tab
  // stop, and Arrow keys move + select + wrap. Without this the four buttons
  // are separate tab stops with no arrow support, which the radiogroup role
  // otherwise promises.
  const selectedIndex = BANDS.findIndex((b) => b.key === value)
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0

  const move = (from: number, delta: number) => {
    const next = (from + delta + BANDS.length) % BANDS.length
    onChange(BANDS[next].key)
    btnRefs.current[next]?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault()
      move(i, 1)
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault()
      move(i, -1)
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Thatch depth">
        {BANDS.map((b, i) => {
          const selected = b.key === value
          return (
            <button
              key={b.key}
              ref={(el) => {
                btnRefs.current[i] = el
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={i === tabbableIndex ? 0 : -1}
              onClick={() => onChange(b.key)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className="flex min-h-16 items-center gap-3.5 rounded-xl py-2 pl-2 pr-3 text-left transition-all duration-200"
              style={{
                background: selected ? "#f0fdf4" : "#fff",
                border: selected ? "1.5px solid #16a34a" : "1.5px solid #e5e7eb",
                boxShadow: selected ? "0 0 0 3px rgba(22,163,74,0.12)" : "none",
              }}
            >
              <BandThumb band={b} selected={selected} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-gray-900">{b.label}</span>
                <span className="mt-px block text-xs text-gray-500">{b.sub}</span>
              </span>
            </button>
          )
        })}
      </div>
      <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-500">
        <svg width="30" height="18" viewBox="0 0 30 18" aria-hidden="true" className="shrink-0">
          <rect x="1" y="4" width="28" height="10" rx="2" fill="#fff" stroke="#0f1d14" strokeWidth="1.2" />
          <path d="M8 4v3M15 4v5M22 4v3" stroke="#0f1d14" strokeWidth="1" />
          <path d="M1 14h14" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
        <span>How to check: cut a small wedge of turf. Your thumbnail is about 1/2 inch wide, use it as the ruler.</span>
      </div>
    </div>
  )
}
